import React, { useState, useEffect } from 'react';
import { Users, Plus, Edit, Trash2, X, CheckCircle, AlertCircle } from 'lucide-react';
import * as api from '../../services/api';

function Customers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showSyncHistory, setShowSyncHistory] = useState(false);
  const [syncHistoryData, setSyncHistoryData] = useState([]);
  const [syncHistoryLoading, setSyncHistoryLoading] = useState(false);
  const [syncHistoryCustomer, setSyncHistoryCustomer] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ username: '', password: '', name: '', email: '', notes: '', xtream_username: '', xtream_password: '', max_connections: 1, provider_lines: [] });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const custRes = await api.getCustomers();
      setCustomers(Array.isArray(custRes.data) ? custRes.data : []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const openCreate = () => { setEditing(null); setForm({ username: '', password: '', name: '', email: '', notes: '', xtream_username: '', xtream_password: '', max_connections: 1, provider_lines: [] }); setShowModal(true); };
  const openEdit = async (customer) => {
    try {
      const res = await api.getCustomer(customer.id);
      const data = res.data;
      setEditing(customer.id);
      setForm({
        username: data.username, password: '', name: data.name || '', email: data.email || '', notes: data.notes || '',
        xtream_username: data.xtream?.xtream_username || '', xtream_password: '', max_connections: data.xtream?.max_connections || 1,
        provider_lines: (data.provider_lines || []).map(pl => ({ id: pl.id, name: pl.name, type: pl.type, host: pl.host, username: pl.username, password: '', priority: pl.priority, is_primary: pl.is_primary }))
      });
      setShowModal(true);
    } catch (err) { alert('Failed to load customer: ' + err.message); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.updateCustomer(editing, form);
      } else {
        await api.createCustomer(form);
      }
      setShowModal(false);
      loadData();
    } catch (err) { alert('Save failed: ' + (err.response?.data?.error || err.message)); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete customer "${name}"? This will also delete their Xtream credentials.`)) return;
    try { await api.deleteCustomer(id); loadData(); }
    catch (err) { alert('Delete failed: ' + err.message); }
  };

  // Phase 3 Item 15: View sync history for a customer
  const viewSyncHistory = async (customer) => {
    setSyncHistoryCustomer(customer);
    setSyncHistoryLoading(true);
    setShowSyncHistory(true);
    try {
      const res = await api.getCustomerSyncHistory(customer.id);
      setSyncHistoryData(res.data || []);
    } catch (e) {
      setSyncHistoryData([]);
    }
    setSyncHistoryLoading(false);
  };

  if (loading) return <div className="page-header"><h2>Loading...</h2></div>;

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div><h2><Users size={24} style={{ marginRight: '8px', verticalAlign: 'middle' }} />Customers</h2></div>
        <button className="btn btn-primary" onClick={openCreate}><Plus size={16} /> Add Customer</button>
      </div>

      <div className="table-container">
        <table>
          <thead><tr><th>Name</th><th>Username</th><th>Xtream User</th><th>Connections</th><th>Provider Lines</th><th>Content</th><th>Status</th><th>Created</th><th>Actions</th></tr></thead>
          <tbody>
            {customers.map(c => (
              <tr key={c.id}>
                <td><strong>{c.name || '-'}</strong></td>
                <td>{c.username}</td>
                <td><code>{c.xtream_username || 'Not set'}</code></td>
                <td>
                  <span title="Active devices / max allowed">{c.active_connections ?? 0} / {c.max_connections ?? 1}</span>
                </td>
                <td>{c.provider_count || 0}</td>
                <td style={{whiteSpace:'nowrap'}}>
                  <span className="badge badge-info" title="Live channels">{((c.channels_count || 0)/1000).toFixed(1)}k Live</span>{' '}
                  <span className="badge badge-success" title="Movies">{((c.movies_count || 0)/1000).toFixed(1)}k Movies</span>{' '}
                  <span className="badge badge-warning" title="Series">{((c.series_count || 0)/1000).toFixed(1)}k Series</span>
                </td>
                <td><span className={`badge badge-${c.enabled ? 'success' : 'danger'}`}>{c.enabled ? <CheckCircle size={14} /> : <AlertCircle size={14} />}{c.enabled ? 'Enabled' : 'Disabled'}</span></td>
                <td>{c.created_at ? new Date(c.created_at + 'Z').toLocaleDateString() : '-'}</td>
                <td>
                  <button className="btn btn-sm btn-secondary" onClick={() => viewSyncHistory(c)} title="Sync History" style={{ marginRight: '5px', background: '#0ea5e9', borderColor: '#0ea5e9', color: '#fff' }}><CheckCircle size={14} /></button>
                  <button className="btn btn-sm btn-secondary" onClick={() => openEdit(c)} title="Edit"><Edit size={14} /></button>
                  <button className="btn btn-sm btn-danger" onClick={() => handleDelete(c.id, c.username)} title="Delete" style={{ marginLeft: '5px' }}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {customers.length === 0 && <tr><td colSpan={9} style={{ textAlign: 'center', color: '#9ca3af' }}>No customers yet</td></tr>}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" style={{ maxWidth: '650px', maxHeight: '80vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3>{editing ? 'Edit Customer' : 'Add Customer'}</h3>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <form onSubmit={handleSave}>
              <div className="form-group"><label>Full Name</label><input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="John Smith" /></div>
              <div className="form-group"><label>Username *</label><input type="text" value={form.username} onChange={e => setForm({...form, username: e.target.value})} required /></div>
              <div className="form-group"><label>Password {editing ? '(leave blank to keep)' : '*'}</label><input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} required={!editing} /></div>
              <div className="form-group"><label>Email</label><input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} /></div>
              <div className="form-group"><label>Notes</label><input type="text" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>
              <hr style={{ margin: '15px 0', border: 'none', borderTop: '1px solid #e5e7eb' }} />
              <h4 style={{ marginBottom: '10px' }}>Xtream Codes Credentials</h4>
              <div className="form-group"><label>Xtream Username</label><input type="text" value={form.xtream_username} onChange={e => setForm({...form, xtream_username: e.target.value})} /></div>
              <div className="form-group"><label>Xtream Password</label><input type="password" value={form.xtream_password} onChange={e => setForm({...form, xtream_password: e.target.value})} /></div>
              <div className="form-group">
                <label>Max Connections</label>
                <input type="number" min="1" max="99" value={form.max_connections} onChange={e => setForm({...form, max_connections: parseInt(e.target.value, 10) || 1})} />
                <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px' }}>
                  Maximum simultaneous streams/devices for this customer&apos;s Xtream login (1–99).
                </p>
              </div>
              <hr style={{ margin: '15px 0', border: 'none', borderTop: '1px solid #e5e7eb' }} />
              <h4 style={{ marginBottom: '10px' }}>Provider Lines ({form.provider_lines.length})</h4>
              <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>Each line is a unique Xtream provider for this customer — content syncs in an isolated worker when streams are idle.</p>
              {form.provider_lines.map((pl, i) => {
                const expanded = pl._expanded !== false;
                return (
                <div key={i} style={{ padding: '10px', background: '#f9fafb', borderRadius: '8px', marginBottom: '8px', border: '1px solid #e5e7eb' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => { const lines = [...form.provider_lines]; lines[i]._expanded = !expanded; setForm({...form, provider_lines: lines}); }}>
                    <div>
                      <strong>{pl.name || 'Unnamed Line'}</strong>
                      <span className="badge badge-info" style={{ marginLeft: '8px', fontSize: '11px' }}>{pl.type || 'xtream'}</span>
                      {pl.is_primary && <span className="badge badge-success" style={{ marginLeft: '8px', fontSize: '11px' }}>Primary</span>}
                      {pl.host && <code style={{ marginLeft: '8px', fontSize: '11px', color: '#6b7280' }}>{pl.host}</code>}
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      {editing && pl.id && !pl.is_primary && (
                        <button type="button" className="btn btn-sm btn-secondary" onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const res = await api.adminSetPrimaryProviderLine(editing, pl.id);
                            const lines = (res.data.provider_lines || []).map(l => ({
                              id: l.id, name: l.name, type: l.type, host: l.host, username: l.username,
                              password: '', priority: l.priority, is_primary: l.is_primary
                            }));
                            setForm(f => ({ ...f, provider_lines: lines }));
                          } catch (err) {
                            alert(err.response?.data?.error || 'Failed to set primary');
                          }
                        }} title="Set as primary">Primary</button>
                      )}
                      {!pl.is_primary && !editing && (
                        <button type="button" className="btn btn-sm btn-secondary" onClick={(e) => {
                          e.stopPropagation();
                          const lines = form.provider_lines.map((l, j) => ({
                            ...l,
                            priority: j === i ? 1 : (j < i ? j + 2 : j + 1),
                            is_primary: j === i
                          }));
                          setForm({ ...form, provider_lines: lines });
                        }} title="Set as primary">Primary</button>
                      )}
                      <button type="button" className="btn btn-sm btn-danger" onClick={(e) => { e.stopPropagation(); setForm({...form, provider_lines: form.provider_lines.filter((_, j) => j !== i)}); }} title="Remove"><Trash2 size={12} /></button>
                    </div>
                  </div>
                  {expanded && (
                    <div style={{ marginTop: '10px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                      <div className="form-group"><label>Name</label><input type="text" value={pl.name} onChange={e => { const lines = [...form.provider_lines]; lines[i].name = e.target.value; setForm({...form, provider_lines: lines}); }} placeholder="e.g. StreamInfinity" /></div>
                      <div className="form-group"><label>Type</label><select value={pl.type} onChange={e => { const lines = [...form.provider_lines]; lines[i].type = e.target.value; setForm({...form, provider_lines: lines}); }} style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db' }}><option value="xtream">Xtream Codes</option><option value="m3u">M3U</option></select></div>
                      <div className="form-group"><label>Host URL</label><input type="text" value={pl.host} onChange={e => { const lines = [...form.provider_lines]; lines[i].host = e.target.value; setForm({...form, provider_lines: lines}); }} placeholder="http://panel.example.com" /></div>
                      <div className="form-group"><label>Username</label><input type="text" value={pl.username} onChange={e => { const lines = [...form.provider_lines]; lines[i].username = e.target.value; setForm({...form, provider_lines: lines}); }} /></div>
                      <div className="form-group" style={{ gridColumn: '1 / -1' }}><label>Password</label><input type="text" value={pl.password} onChange={e => { const lines = [...form.provider_lines]; lines[i].password = e.target.value; setForm({...form, provider_lines: lines}); }} /></div>
                    </div>
                  )}
                </div>
              );})}
              <button type="button" className="btn btn-sm btn-primary" onClick={() => setForm({...form, provider_lines: [...form.provider_lines, { name: '', type: 'xtream', host: '', username: '', password: '', _expanded: true }]})} style={{ marginBottom: '15px' }}>+ Add Provider Line</button>

              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>{editing ? 'Update Customer' : 'Create Customer'}</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Sync History Modal */}
      {showSyncHistory && (
        <div className="modal-overlay" onClick={() => setShowSyncHistory(false)}>
          <div className="modal-content" style={{ maxWidth: '900px', maxHeight: '80vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0 }}>Sync History — {syncHistoryCustomer?.name}</h3>
              <button onClick={() => setShowSyncHistory(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px' }}>✕</button>
            </div>
            {syncHistoryLoading ? (
              <p>Loading...</p>
            ) : syncHistoryData.length === 0 ? (
              <p>No sync history found.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                    <th style={{ padding: '8px' }}>Line</th>
                    <th style={{ padding: '8px' }}>Status</th>
                    <th style={{ padding: '8px' }}>Started</th>
                    <th style={{ padding: '8px' }}>Completed</th>
                    <th style={{ padding: '8px' }}>Ch/M/S</th>
                    <th style={{ padding: '8px' }}>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {syncHistoryData.map(row => (
                    <tr key={row.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px' }}>{row.line_name || '-'}</td>
                      <td style={{ padding: '8px' }}>
                        <span style={{
                          padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600,
                          background: row.status === 'completed' ? '#dcfce7' : row.status === 'failed' ? '#fee2e2' : '#fef9c3',
                          color: row.status === 'completed' ? '#166534' : row.status === 'failed' ? '#991b1b' : '#854d0e'
                        }}>{row.status}</span>
                      </td>
                      <td style={{ padding: '8px', fontSize: '12px' }}>{row.started_at ? new Date(row.started_at).toLocaleString() : '-'}</td>
                      <td style={{ padding: '8px', fontSize: '12px' }}>{row.completed_at ? new Date(row.completed_at).toLocaleString() : '-'}</td>
                      <td style={{ padding: '8px', fontSize: '12px' }}>{row.channels ?? '-'}/{row.movies ?? '-'}/{row.series ?? '-'}</td>
                      <td style={{ padding: '8px', fontSize: '11px', color: '#dc2626', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.error_message || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Customers;
